import { TextSelection, NodeSelection } from 'prosemirror-state';
import { BLOCK_CLASSES, TEXT_BLOCKS } from '@intercom/composer-ember/lib/blocks/constants';
import { adjustForIframeIfNecessary } from './iframe';
import { findChildren, findParentNode, findParentNodeOfType } from 'prosemirror-utils';
export function findSelectedListNode(editorState) {
  let {
    schema,
    selection
  } = editorState;
  let listNodes = [schema.nodes.unorderedList, schema.nodes.orderedList];
  return findParentNodeOfType(listNodes)(selection);
}
export function findSelectedSection(editorState) {
  let sectionPredicate = node => {
    return node.type === editorState.schema.nodes.table && node.attrs.container;
  };
  return findParentNode(sectionPredicate)(editorState.selection);
}
export function findSelectedCallout(editorState) {
  let {
    schema,
    selection
  } = editorState;
  return findParentNodeOfType(schema.nodes.callout)(selection);
}
export function findSelectedCell(editorState) {
  let {
    schema,
    selection
  } = editorState;
  return findParentNodeOfType(schema.nodes.tableCell)(selection);
}
export function findSelectedTableCell(editorState) {
  let sectionPredicate = node => {
    return node.type === editorState.schema.nodes.tableCell && node.attrs.container;
  };
  let selectedSection = findSelectedSection(editorState);
  if (selectedSection) {
    //A section as only one cell, so select the first children
    let nodeWithPos = findChildren(selectedSection.node, sectionPredicate)[0];
    //Set the absolute position
    nodeWithPos.pos += selectedSection.start;
    return nodeWithPos;
  }
  return undefined;
}
export function selectAllTextInBlock(state, dispatch, node, nodePosition) {
  if (!TEXT_BLOCKS.includes(node.attrs.type)) {
    return;
  }
  let listParent = findSelectedListNode(state);
  // the content starts at nodePosition + 1 for all text blocks (e.g. 1 is the size of the <p> tag)
  let selectionStart = listParent ? listParent.start : nodePosition + 1;
  let selectionLength = listParent ? listParent.node.content.size : node.content.size;
  let newSelection = TextSelection.create(state.doc, selectionStart, selectionStart + selectionLength);
  dispatch(state.tr.setSelection(newSelection));
}
export function findSelectionAnchorNode(editorState) {
  let {
    schema,
    selection
  } = editorState;
  let parentNodes = [schema.nodes.doc, schema.nodes.tableCell];
  if (selection instanceof NodeSelection) {
    return selection.node;
  }
  if (parentNodes.includes(selection.$from.parent.type) && selection.$anchor.nodeAfter) {
    return selection.$anchor.nodeAfter;
  }
  let parentNode = findSelectedListNode(editorState);
  if (parentNode) {
    return parentNode?.node;
  }
  return selection.$from.parent;
}
export function selectionInfo(editorView) {
  let rect;
  if (editorView.state.selection instanceof TextSelection) {
    rect = textSelectionRect(editorView);
  } else {
    rect = nodeSelectionRect(editorView);
  }
  let visible = isVisible(rect, editorView);
  rect = adjustForIframeIfNecessary(rect, editorView);
  return {
    rect,
    visible
  };
}
export function findMark($pos, markType) {
  return $pos.marks().find(mark => mark.type === markType);
}
export function getMarkBounds(doc, mark) {
  let from;
  let to = 0;
  doc.descendants((node, pos, _) => {
    if (node.marks.includes(mark)) {
      if (!from) {
        from = pos;
      }
      to = pos + node.nodeSize;
      return false;
    }
    return true;
  });
  if (from === undefined) {
    from = 0;
  }
  return {
    from,
    to
  };
}
export function selectionHasMark(state, type) {
  if (state === undefined || type === undefined) {
    return false;
  }
  let {
    from,
    $from,
    to,
    empty
  } = state.selection;
  if (empty) {
    return Boolean(type.isInSet($from.marks()));
  } else {
    return state.doc.rangeHasMark(from, to, type);
  }
}
export function startOfBlock(state) {
  let $cursor = state.selection.$cursor;
  return $cursor && $cursor.parentOffset === 0;
}
function nodeSelectionRect(editorView) {
  let {
    from
  } = editorView.state.selection;
  let dom = editorView.domAtPos(from);
  // We should only be in here when a node is selected, hence the type assertion
  let element = dom.node.childNodes.item(dom.offset);
  if (element.classList.contains(BLOCK_CLASSES.blockContainer)) {
    element = element.children[0];
  }
  return element.getBoundingClientRect();
}
function textSelectionRect(editorView) {
  let {
    from,
    to
  } = editorView.state.selection;
  let start = editorView.coordsAtPos(from);
  let end = editorView.coordsAtPos(to);
  let top = Math.min(start.top, end.top);
  let right = Math.max(start.right, end.right);
  let bottom = Math.max(start.bottom, end.bottom);
  let left = Math.min(start.left, end.left);
  let width = right - left;
  let height = bottom - top;
  return new DOMRect(left, top, width, height);
}
function isVisible(rect, editorView) {
  let editorRect = editorView.dom.getBoundingClientRect();
  return rect.top <= editorRect.bottom && rect.bottom >= editorRect.top;
}